/**
 * <size-selector-toggle>
 *
 * Wraps the floating size selector panel and its open trigger inside a product card.
 * Handles tap-to-open on mobile, tap-outside / ESC / cart:change to close, and
 * mutual exclusion (only one card's panel open at a time).
 *
 * Desktop hover behavior lives purely in CSS (component-size-selector.css) and is
 * gated to `@media (pointer: fine)` so sticky-hover on touch never traps the panel open.
 *
 * Positioning note: product-card must have position:relative so the absolute panel
 * anchors to the card. The figure has position:static (see :has rule in CSS) specifically
 * so the panel spans the full card width, not just the figure.
 */

class SizeSelectorToggle extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._productCard = null;
    this._openTrigger = null;
    this._panel = null;

    this._onOpenClick = this._onOpenClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._close = this._close.bind(this);
  }

  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._productCard = this.closest('product-card');
    this._openTrigger = this.querySelector('.size-selector-open-trigger');
    this._panel = this.querySelector('.product-card__floating-size-selector');
    const closeTriggers = this.querySelectorAll('.size-selector-close-trigger');

    if (!this._productCard || !this._openTrigger || !this._panel) {
      return;
    }

    this._openTrigger.addEventListener('click', this._onOpenClick, { signal });
    closeTriggers.forEach((btn) => {
      btn.addEventListener('click', this._onCloseClick, { signal });
    });

    document.addEventListener('click', this._onDocumentClick, { signal });
    document.addEventListener('keydown', this._onKeyDown, { signal });
    document.documentElement.addEventListener('cart:change', this._close, { signal });
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._abortController = null;
  }

  _isOpen() {
    return this._productCard?.classList.contains('is-open') ?? false;
  }

  _onOpenClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    // Close any other open card first
    document.querySelectorAll('product-card.is-open').forEach((card) => {
      if (card === this._productCard) return;
      card.classList.remove('is-open');
      card.querySelector('.size-selector-open-trigger')?.setAttribute('aria-expanded', 'false');
    });

    // Always open — never toggle-close. CSS hides this trigger when panel is open,
    // so the user can only close via the X button or an outside tap.
    this._productCard.classList.add('is-open');
    this._openTrigger.setAttribute('aria-expanded', 'true');
  }

  _onCloseClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this._close();
  }

  _onDocumentClick(event) {
    if (!this._isOpen()) return;
    if (this.contains(event.target)) return;
    this._close();
  }

  _onKeyDown(event) {
    if (event.key !== 'Escape') return;
    if (!this._isOpen()) return;
    this._close();
    this._openTrigger.focus();
  }

  _close() {
    if (!this._isOpen()) return;
    this._productCard.classList.remove('is-open');
    this._openTrigger.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('size-selector-toggle')) {
  customElements.define('size-selector-toggle', SizeSelectorToggle);
}
